<template>
  <div>
    <!-- Header -->
    <div class="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
      <div class="flex items-center gap-4 min-w-0 flex-1">
        <button
          type="button"
          class="p-3 rounded-md bg-depth-0 text-rhino hover:text-brand shrink-0"
          :aria-label="$t('support/ticket_detail/back')"
          @click="emit('close')"
        >
          <fa-icon :icon="faChevronLeft" class="size-4" />
        </button>

        <h2
          class="truncate text-lg md:text-xl font-semibold text-mouse m-0 leading-tight uppercase"
          :title="currentTicket.title"
          :aria-label="currentTicket.title"
          data-testid="support-ticket-title"
        >
          {{ currentTicket.title }}
        </h2>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <slot name="actions">
          <common-button
            v-if="currentTicket.isClosable"
            variant="secondary"
            outline
            size="sm"
            class="whitespace-nowrap"
            :disabled="isClosing || isClosed"
            :aria-label="$t('support/ticket_detail/close')"
            data-testid="support-ticket-close"
            @click="handleClose"
          >
            <span v-if="!isClosing">
              {{ $t('support/ticket_detail/close') }}
            </span>
            <span v-else>{{ $t('support/ticket_detail/closing') }}</span>
          </common-button>
        </slot>
      </div>
    </div>

    <!-- Description -->
    <div
      class="mt-2 flex flex-wrap items-center gap-2 text-sm md:text-base text-rhino"
    >
      <span>{{ currentTicket.id }}</span>
      <span aria-hidden="true">•</span>

      <span class="inline-flex items-center gap-2">
        <span
          class="inline-block h-2.5 w-2.5 rounded-full"
          :class="isClosed ? 'bg-rhino' : 'bg-success'"
        ></span>
        <span
          class="inline-flex px-2 py-0.5 rounded-md font-medium"
          :class="
            isClosed ? 'bg-rhino/10 text-rhino' : 'bg-success/10 text-success'
          "
          data-testid="support-ticket-status"
        >
          {{
            $t(`support/ticket_status/${currentTicket.status || 'unspecified'}`)
          }}
        </span>
      </span>

      <span aria-hidden="true">•</span>
      <span>{{ dt(currentTicket.created) }}</span>

      <!-- TODO: Re-enable once backend adds 'modified' or 'updated_at' field to Ticket proto -->
      <!-- <template v-if="currentTicket.modified">
        <span aria-hidden="true">•</span>
        <span>
          {{ $t('support/ticket_detail/updated') }}
          {{ dt(currentTicket.modified) }}
        </span>
      </template> -->

      <span aria-hidden="true">•</span>
      <span class="text-rhino">{{ currentTicket.category.name }}</span>
    </div>

    <!-- Messages -->
    <div class="mt-8">
      <div v-if="loading" class="space-y-4">
        <div v-for="i in 3" :key="i" class="animate-pulse">
          <div class="h-4 w-28 bg-depth-0 rounded-md mb-2"></div>
          <div class="h-16 bg-depth-0 rounded-md"></div>
        </div>
      </div>

      <div
        v-else-if="currentMessages && currentMessages.length"
        class="space-y-8"
      >
        <div
          v-for="m in currentMessages"
          :key="m.id"
          class="flex flex-col gap-2"
          :class="m.isCustomer ? 'items-start' : 'items-end'"
        >
          <div class="md:text-sm text-rhino">
            <span
              class="font-bold"
              :class="m.isCustomer ? 'text-mouse' : 'text-brand'"
            >
              {{
                m.isCustomer
                  ? m.customer.userName || $t('support/ticket_detail/you')
                  : m.customer.userName || $t('support/ticket_detail/support')
              }}
            </span>
            <span aria-hidden="true" class="px-1">•</span>
            <span>{{ dt(m.created) }}</span>
          </div>

          <div
            class="max-w-11/12 md:max-w-10/12 whitespace-pre-wrap break-words hyphens-auto px-4 py-3 rounded-lg shadow-sm overflow-hidden"
            :class="
              m.isCustomer
                ? 'bg-depth-0 text-mouse'
                : 'bg-depth-0/75 text-rhino'
            "
            v-html="m.message"
          ></div>

          <!-- Attachments. Both directions: a customer's screenshot and an agent's config file come
               back through the same field. `download` asks the browser to save rather than navigate,
               and noreferrer keeps the (capability-like) Intercom URL out of the Referer header. -->
          <ul
            v-if="m.attachments.length > 0"
            class="max-w-11/12 md:max-w-10/12 flex flex-wrap gap-2"
          >
            <li v-for="a in m.attachments" :key="a.url">
              <!-- Images render as themselves: a screenshot is the single most common attachment on
                   a support ticket, and a filename tells nobody anything about it. Everything else
                   stays a download link. -->
              <button
                v-if="isInlineImage(a.name, a.contentType)"
                type="button"
                class="block rounded-lg overflow-hidden bg-depth-0 hover:opacity-90"
                :aria-label="a.name"
                @click="openLightbox(a)"
              >
                <img
                  :src="a.url"
                  :alt="a.name"
                  class="block w-[200px] h-auto"
                  loading="lazy"
                />
              </button>

              <a
                v-else
                :href="a.url"
                :download="a.name"
                target="_blank"
                rel="noreferrer noopener"
                class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-depth-0 text-sm text-mouse hover:text-brand"
              >
                <fa-icon
                  :icon="attachmentIcon(a.name, a.contentType)"
                  class="size-4 shrink-0 text-rhino"
                />
                <span class="break-all">{{ a.name }}</span>
                <span class="text-rhino shrink-0">{{
                  formatFilesize(a.filesize)
                }}</span>
              </a>
            </li>
          </ul>

          <!-- Rating feedback (legacy tickets only; Intercom ratings live in the Messenger widget) -->
          <div
            v-if="!m.isCustomer && ratingsEnabled"
            class="flex items-center gap-2 text-sm self-end"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-depth-0"
              :class="
                m.rating === 'positive'
                  ? 'text-brand'
                  : 'text-rhino hover:text-brand'
              "
              :disabled="isRating"
              :aria-label="$t('support/ticket_rating/positive')"
              @click="handleRating(m.id, 'positive')"
            >
              <svg
                class="size-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                fill="currentColor"
              >
                <path
                  d="M144 224C161.7 224 176 238.3 176 256L176 512C176 529.7 161.7 544 144 544L96 544C78.3 544 64 529.7 64 512L64 256C64 238.3 78.3 224 96 224L144 224zM334.6 80C361.9 80 384 102.1 384 129.4L384 133.6C384 140.4 382.7 147.2 380.2 153.5L352 224L512 224C538.5 224 560 245.5 560 272C560 291.7 548.1 308.6 531.1 316C548.1 323.4 560 340.3 560 360C560 383.4 543.2 402.9 521 407.1C525.4 414.4 528 422.9 528 432C528 454.2 513 472.8 492.6 478.3C494.8 483.8 496 489.8 496 496C496 522.5 474.5 544 448 544L360.1 544C323.8 544 288.5 531.6 260.2 508.9L248 499.2C232.8 487.1 224 468.7 224 449.2L224 262.6C224 247.7 227.5 233 234.1 219.7L290.3 107.3C298.7 90.6 315.8 80 334.6 80z"
                />
              </svg>
              <span class="hidden sm:inline">
                {{ $t('support/ticket_rating/positive') }}
              </span>
            </button>

            <button
              type="button"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-depth-0"
              :class="
                m.rating === 'negative'
                  ? 'text-danger'
                  : 'text-rhino hover:text-danger'
              "
              :disabled="isRating"
              :aria-label="$t('support/ticket_rating/negative')"
              @click="handleRating(m.id, 'negative')"
            >
              <svg
                class="size-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                fill="currentColor"
              >
                <path
                  d="M448 96C474.5 96 496 117.5 496 144C496 150.3 494.7 156.2 492.6 161.7C513 167.2 528 185.8 528 208C528 217.1 525.4 225.6 521 232.9C543.2 237.1 560 256.6 560 280C560 299.7 548.1 316.6 531.1 324C548.1 331.4 560 348.3 560 368C560 394.5 538.5 416 512 416L352 416L380.2 486.4C382.7 492.7 384 499.5 384 506.3L384 510.5C384 537.8 361.9 559.9 334.6 559.9C315.9 559.9 298.8 549.3 290.4 532.6L234.1 420.3C227.4 407 224 392.3 224 377.4L224 190.8C224 171.4 232.9 153 248 140.8L260.2 131.1C288.6 108.4 323.8 96 360.1 96L448 96zM144 160C161.7 160 176 174.3 176 192L176 448C176 465.7 161.7 480 144 480L96 480C78.3 480 64 465.7 64 448L64 192C64 174.3 78.3 160 96 160L144 160z"
                />
              </svg>
              <span class="hidden sm:inline">
                {{ $t('support/ticket_rating/negative') }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="py-16 text-center text-rhino">
        {{ $t('support/ticket_detail/no_messages') }}
      </div>
    </div>

    <!-- Full-size image, following the modal pattern in PaymentMethodModal.vue. `v-if` rather than
         v-show so the full-size <img> is not fetched until it is asked for — the thumbnail already
         costs a request, and these are customer uploads of unknown size. -->
    <div
      v-if="lightbox"
      class="fixed inset-0 z-50 flex items-center justify-center bg-onyx/80 p-4"
      role="dialog"
      aria-modal="true"
      :aria-label="lightbox.name"
      @click="closeLightbox"
    >
      <button
        type="button"
        class="absolute top-4 right-4 text-mouse hover:text-brand text-3xl leading-none"
        :aria-label="$t('frontend/close')"
        @click="closeLightbox"
      >
        ×
      </button>

      <!-- Clicks on the image itself must not close it; only the backdrop and the button do. -->
      <img
        :src="lightbox.url"
        :alt="lightbox.name"
        class="max-w-full max-h-full object-contain"
        @click.stop
      />
    </div>

    <!-- Reply -->
    <div v-if="enableReply && !isClosed" class="mt-4 md:mt-8">
      <form class="space-y-2" @submit.prevent="handleSend">
        <gp-input
          :label="$t('support/ticket_detail/reply_label')"
          :hint="$t('support/ticket_detail/reply_hint')"
          input-id="reply"
          is-textarea
        >
          <textarea
            id="reply"
            v-model="draft"
            rows="4"
            class="py-2 w-full border-b border-rhino/50 placeholder-rhino text-mouse focus:outline-none"
            required
          ></textarea>
        </gp-input>

        <!-- Attachments are an Intercom-only capability; the API rejects them on a legacy ticket, so
             the control is not offered there at all rather than offered and refused. -->
        <div v-if="attachmentsEnabled" class="space-y-2">
          <label for="reply-attachments" class="block text-sm text-rhino">
            {{
              $t('support/ticket_detail/attachment_label', {
                max: MAX_ATTACHMENTS,
                size: formatFilesize(MAX_ATTACHMENT_BYTES),
              })
            }}
          </label>
          <input
            id="reply-attachments"
            ref="fileInput"
            type="file"
            multiple
            :accept="ATTACHMENT_ACCEPT"
            class="block w-full text-sm text-rhino file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-depth-0 file:text-mouse hover:file:text-brand"
            @change="onFilesSelected"
          />

          <p v-if="attachmentError" class="text-sm text-danger">
            {{ attachmentError }}
          </p>

          <ul v-if="pendingFiles.length > 0" class="space-y-1 text-sm">
            <li
              v-for="(f, i) in pendingFiles"
              :key="`${f.name}-${i}`"
              class="flex items-center justify-between gap-2 text-mouse"
            >
              <span class="flex items-center gap-2 min-w-0">
                <fa-icon
                  :icon="attachmentIcon(f.name, f.type)"
                  class="size-4 shrink-0 text-rhino"
                />
                <span class="break-all">{{ f.name }}</span>
              </span>
              <span class="flex items-center gap-2 shrink-0">
                <span class="text-rhino">{{ formatFilesize(f.size) }}</span>
                <button
                  type="button"
                  class="text-rhino hover:text-danger"
                  @click="removePendingFile(i)"
                >
                  {{ $t('support/ticket_detail/attachment_remove') }}
                </button>
              </span>
            </li>
          </ul>
        </div>

        <div class="flex items-center justify-end gap-2">
          <common-button
            variant="secondary"
            outline
            size="sm"
            @click="clearDraft"
          >
            {{ $t('support/ticket_detail/clear') }}
          </common-button>
          <common-button
            type="submit"
            size="sm"
            :disabled="!draft.trim() || isBusy"
          >
            {{ $t('support/ticket_detail/send') }}
          </common-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import useDateTime from '@/composables/useDateTime';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import {
  ATTACHMENT_ACCEPT,
  isAllowedAttachment,
} from '~/helpers/attachment-extensions';
import { attachmentIcon, isInlineImage } from '~/helpers/attachment-icon';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import GpInput from '~/components/support/GpInput.vue';
import { useSupportStore } from '~/store/support';
import type { MessageRating } from '~/store/support/protoHelpers';
import type {
  Ticket,
  TicketAttachmentUpload,
  TicketMessage,
  TicketMessageAttachment,
} from '~/store/support/types';

const props = withDefaults(
  defineProps<{
    ticket: Ticket;
    messages: TicketMessage[];
    loading?: boolean;
    enableReply?: boolean;
    // An awaited callback rather than a `send` event, because the outcome matters here: a `void`
    // emit returns at the parent's first await, so this component could only ever clear the draft
    // optimistically — and on a failure the customer would be left with an error toast, an empty
    // textarea and an empty file list, having to re-pick every file.
    send: (
      body: string,
      attachments: TicketAttachmentUpload[],
    ) => Promise<boolean>;
  }>(),
  {
    loading: false,
    enableReply: true,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const supportStore = useSupportStore();
const { t } = useI18n();

const currentTicket = ref<Ticket>(props.ticket);
const currentMessages = ref<TicketMessage[]>(props.messages || []);

watch(
  () => props.ticket,
  (v) => {
    currentTicket.value = v;
  },
  { immediate: true },
);
watch(
  () => props.messages,
  (v) => {
    currentMessages.value = v || [];
  },
  { immediate: true },
);

watch(
  () => supportStore.messagesByTicket[currentTicket.value.id],
  (messages) => {
    if (messages) currentMessages.value = messages;
  },
);

const isClosed = computed<boolean>(
  () => currentTicket.value.status === 'closed',
);

// Per-message thumbs up/down only applies to legacy tickets. Intercom conversation ratings are
// submitted by the customer inside the Messenger/Fin widget and are read-only via the API, so the
// per-message rating UI is hidden for Intercom-sourced tickets.
const ratingsEnabled = computed<boolean>(
  () => currentTicket.value.source !== 'intercom',
);

const { formatFn } = useDateTime();
const dt = (v: string | number | Date) => formatFn(v);

const draft = ref<string>('');
const isClosing = ref<boolean>(false);
const isRating = ref<boolean>(false);

// ---------------------------------------------------------------------------
// Attachments
//
// Intercom-only: the API answers UNIMPLEMENTED for attachments on a legacy ticket, so the control is
// hidden rather than offered and refused.
const attachmentsEnabled = computed<boolean>(
  () => currentTicket.value.source === 'intercom',
);

// Both limits are enforced again server-side. These exist so a customer finds out before waiting for
// an upload, and finds out WHICH file — not for safety, which a browser cannot provide.
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

const fileInput = ref<HTMLInputElement | null>(null);
const pendingFiles = ref<File[]>([]);
const attachmentError = ref<string>('');
const isSending = ref<boolean>(false);

// The attachment shown full size, or null. Escape closes it, which is what a dialog is expected to
// do and the only way out for a keyboard user who never reaches the close button.
const lightbox = ref<TicketMessageAttachment | null>(null);

const onLightboxKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    lightbox.value = null;
  }
};

const openLightbox = (attachment: TicketMessageAttachment): void => {
  lightbox.value = attachment;
  window.addEventListener('keydown', onLightboxKeydown);
};

const closeLightbox = (): void => {
  lightbox.value = null;
  window.removeEventListener('keydown', onLightboxKeydown);
};

// Navigating away with the lightbox open would otherwise leave the listener behind.
onUnmounted(() => {
  window.removeEventListener('keydown', onLightboxKeydown);
});

const formatFilesize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const onFilesSelected = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const selected = Array.from(input.files ?? []);
  attachmentError.value = '';

  for (const file of selected) {
    // Checked before size, because it is the likelier rejection and the cheaper one. Intercom
    // refuses a disallowed extension with a 403 that fails the whole reply, message included, so a
    // .jar or a .webp would otherwise cost the customer everything they had typed.
    if (!isAllowedAttachment(file.name)) {
      attachmentError.value = t('support/ticket_detail/attachment_type', {
        name: file.name,
      });
      continue;
    }
    // Rejected here because the proto declares `bytes data = 3 [(buf.validate.field).bytes.min_len
    // = 1]`, so ProtoValidation fails the WHOLE reply with INVALID_ARGUMENT before the handler that
    // would name the offending file ever runs — the customer just gets the generic send error and
    // retries the same doomed request. An empty file is not exotic: it is what a server that never
    // started writes as its log, which is exactly the file someone attaches to a support ticket.
    if (file.size === 0) {
      attachmentError.value = t('support/ticket_detail/attachment_empty', {
        name: file.name,
      });
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      // Named, because a customer who picked three files needs to know which one to swap out.
      attachmentError.value = t('support/ticket_detail/attachment_too_large', {
        name: file.name,
        max: formatFilesize(MAX_ATTACHMENT_BYTES),
      });
      continue;
    }
    if (pendingFiles.value.length >= MAX_ATTACHMENTS) {
      attachmentError.value = t('support/ticket_detail/attachment_too_many', {
        max: MAX_ATTACHMENTS,
      });
      break;
    }
    pendingFiles.value.push(file);
  }

  // Reset the input so re-picking the same file after removing it still fires a change event.
  if (input) input.value = '';
};

const removePendingFile = (index: number): void => {
  pendingFiles.value.splice(index, 1);
  attachmentError.value = '';
};

const clearDraft = (): void => {
  draft.value = '';
  pendingFiles.value = [];
  attachmentError.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

// The proto field is `bytes`, so the raw file goes on the wire as-is; the base64 Intercom wants is
// applied once, server-side. Nothing here encodes anything.
const readFile = async (file: File): Promise<TicketAttachmentUpload> => ({
  name: file.name,
  contentType: file.type || 'application/octet-stream',
  data: new Uint8Array(await file.arrayBuffer()),
});

// Covers the whole round trip — reading the files here and the request the parent makes — because
// the callback is awaited. A second submit while either is in flight is blocked.
const isBusy = computed<boolean>(() => isSending.value);

const handleSend = async (): Promise<void> => {
  const body = draft.value.trim();
  if (!body || isBusy.value) return;

  isSending.value = true;
  try {
    const attachments = await Promise.all(pendingFiles.value.map(readFile));

    // Only on confirmed success. The parent surfaces its own error; what must not happen is the
    // draft and the files disappearing along with it.
    if (await props.send(body, attachments)) {
      clearDraft();
    }
  } catch {
    attachmentError.value = t('support/ticket_detail/attachment_read_failed');
  } finally {
    isSending.value = false;
  }
};

const handleRating = async (
  messageId: string,
  rating: MessageRating,
): Promise<void> => {
  if (isRating.value) return;

  const message = currentMessages.value.find((m) => m.id === messageId);
  if (message?.rating && message.rating !== 'unspecified') return;

  isRating.value = true;
  try {
    await supportStore.rateSupportMessage(
      messageId,
      rating,
      currentTicket.value.id,
    );
  } finally {
    isRating.value = false;
  }
};

const handleClose = async (): Promise<void> => {
  if (isClosing.value || isClosed.value) return;
  isClosing.value = true;

  const prevStatus = currentTicket.value.status;
  const prevModified = currentTicket.value.modified;
  currentTicket.value = {
    ...currentTicket.value,
    status: 'closed',
    modified: new Date().toISOString(),
  };

  try {
    const ok = await supportStore.closeTicket(currentTicket.value.id);
    if (!ok) {
      currentTicket.value.status = prevStatus;
      currentTicket.value.modified = prevModified;
      return;
    }
    await supportStore.fetchTicketById(currentTicket.value.id);
    const updated = (supportStore.ticketsList || []).find(
      (tk: Ticket) => tk.id === currentTicket.value.id,
    );
    if (updated) currentTicket.value = updated;
    const msgs = supportStore.messagesByTicket?.[currentTicket.value.id];
    if (msgs) currentMessages.value = msgs;
  } finally {
    isClosing.value = false;
  }
};
</script>
